import { createAsyncThunk } from "@reduxjs/toolkit";
import * as API from "../../services/portalService";
import _ from "lodash";

/** Query string for the order list, built from the shared `state.portal` filters. */
const buildOrderQueryParams = (portal: any): URLSearchParams => {
  const {
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
    subOrganizationFilter,
  } = portal;

  const queryParams = new URLSearchParams();

  // Pagination
  queryParams.append("page", page.toString());
  queryParams.append("pageSize", pageSize.toString());

  // Search
  if (searchQuery.trim()) {
    queryParams.append("search", searchQuery.trim());
  }

  // Category/status filter (supports multi-select via comma-separated values)
  if (categoryFilter && categoryFilter !== "all") {
    const statuses = categoryFilter.split(",");
    statuses.forEach((s: string) => {
      if (s.trim()) queryParams.append("status", s.trim());
    });
  }

  if (subOrganizationFilter && subOrganizationFilter !== "all") {
    queryParams.append("subOrganization", subOrganizationFilter);
  }

  // Sorting
  switch (sortBy) {
    case "name-asc":
      queryParams.append("sort", "name");
      queryParams.append("order", "asc");
      break;
    case "name-desc":
      queryParams.append("sort", "name");
      queryParams.append("order", "desc");
      break;
    case "created-asc":
      queryParams.append("sort", "createdAt");
      queryParams.append("order", "asc");
      break;
    case "created-desc":
    default:
      queryParams.append("sort", "createdAt");
      queryParams.append("order", "desc");
      break;
  }

  if (requestType && requestType !== "all") {
    const types = requestType.split(",");
    types.forEach((t: string) => {
      if (t.trim()) queryParams.append("requestType", t.trim());
    });
  }

  if (dateFilter && dateFilter !== "all") {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let dateFrom = new Date(today);
    let dateTo = new Date(today);

    if (dateFilter.startsWith("Custom:")) {
      const range = dateFilter.replace("Custom:", "").split("_");
      if (range.length === 2) {
        dateFrom = new Date(range[0]);
        dateTo = new Date(range[1]);
      }
    } else {
      switch (dateFilter) {
        case "Yesterday":
          dateFrom.setDate(dateFrom.getDate() - 1);
          dateTo = new Date(today); // dateTo is today so we include yesterday fully
          break;
        case "Last 7 days":
          dateFrom.setDate(dateFrom.getDate() - 7);
          dateTo = new Date(today);
          break;
        case "Today":
          dateTo.setDate(dateTo.getDate() + 1);
          break;
        case "Bi-Weekly":
          dateFrom.setDate(dateFrom.getDate() - 7);
          dateTo.setDate(dateTo.getDate() + 7);
          break;
        case "Next 7 days":
          dateTo.setDate(dateTo.getDate() + 7);
          break;
        case "Next 14 days":
          dateTo.setDate(dateTo.getDate() + 14);
          break;
        case "Next 30 days":
          dateTo.setDate(dateTo.getDate() + 30);
          break;
        default:
          break;
      }
    }

    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    queryParams.append("dateFrom", formatLocalDate(dateFrom));
    queryParams.append("dateTo", formatLocalDate(dateTo));
  }

  return queryParams;
};

export const getOrders = createAsyncThunk(
  "portal/orders/list",
  async (_, { getState }) => {
    const state: any = getState();
    const response = await API.getPortalSalesOrder(
      buildOrderQueryParams(state.portal),
    );
    return response.data;
  },
);

/**
 * One organization's orders, for admins/owners, operation and that org's
 * sales reps. Uses the same filters and the same `state.portal.orders` slot
 * as getOrders.
 */
export const getOrganizationOrders = createAsyncThunk(
  "portal/admin/organization/orders/list",
  async (organizationId: string, { getState }) => {
    const state: any = getState();
    const queryParams = buildOrderQueryParams(state.portal);
    queryParams.append("organization", organizationId);
    const response = await API.getGoSalesOrders(queryParams);
    return response.data;
  },
);

export const getPortalReportHistory = createAsyncThunk(
  "portal/issues/history",
  async (_, { getState }) => {
    try {
      const state: any = getState();
      const {
        page,
        pageSize,
        searchQuery,
        categoryFilter,
        sortBy,
        issueType,
        subOrganizationFilter,
      } = state.portal.issues;

      const queryParams = new URLSearchParams();

      // Pagination
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      // Search
      if (searchQuery.trim()) {
        queryParams.append("search", searchQuery.trim());
      }

      // Category/status filter (supports multi-select via comma-separated values)
      if (categoryFilter && categoryFilter !== "all") {
        const statuses = categoryFilter.split(",");
        statuses.forEach((s: string) => {
          if (s.trim()) queryParams.append("status", s.trim());
        });
      }

      if (subOrganizationFilter && subOrganizationFilter !== "all") {
        queryParams.append("subOrganization", subOrganizationFilter);
      }

      // Sorting
      switch (sortBy) {
        case "name-asc":
          queryParams.append("sort", "name");
          queryParams.append("order", "asc");
          break;
        case "name-desc":
          queryParams.append("sort", "name");
          queryParams.append("order", "desc");
          break;
        case "created-asc":
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "asc");
          break;
        case "created-desc":
        default:
          queryParams.append("sort", "createdAt");
          queryParams.append("order", "desc");
          break;
      }

      if (issueType && issueType !== "all") {
        const types = issueType.split(",");
        types.forEach((t: string) => {
          if (t.trim()) queryParams.append("issueType", t.trim());
        });
      }

      const response = await API.getPortalIssuesHistory(queryParams);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const retrieveIssueSummary = createAsyncThunk(
  "portal/issues/summary",
  async (issueId: string, { rejectWithValue }) => {
    try {
      const { data } = await API.retrieveIssueSummary(issueId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createIssueOrder = createAsyncThunk(
  "portal/issues/create/order",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.createIssueOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const patchIssueOrder = createAsyncThunk(
  "portal/issues/update/order",
  async (
    { status, issueId }: { status: string; issueId: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.updateIssueOrder(issueId, status);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const shareIssueToOperation = createAsyncThunk(
  "portal/issues/share-operation",
  async ({ payload }: { payload: any }, { rejectWithValue }) => {
    try {
      const { data } = await API.shareIssueToOperation(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const manualUpdateCustomerOrderStatus = createAsyncThunk(
  "portal/customer/manual-update-order-status",
  async (
    { _id, payload }: { _id: string; payload: any },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.manualUpdateCustomerOrderStatus(_id, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllAppointments = createAsyncThunk(
  "portal/delivery/appointments",
  async (
    params: {
      startDate: string;
      endDate: string;
      timezone?: string;
      warehouse?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await API.getAllPortalDeliveryAppointments(params);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getWarehouseList = createAsyncThunk(
  "portal/delivery/warehouse-list",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getWarehouseList();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Superadmin dispatchers
export const getOrganizations = createAsyncThunk(
  "portal/admin/getOrganizations",
  async (_, { getState }) => {
    try {
      const state: any = getState();
      const { page, pageSize, searchQuery, categoryFilter, sortBy } =
        state.portal.admin.organizations;

      const params: Record<string, any> = {
        page,
        pageSize,
      };

      // Search
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Category/status filter
      if (categoryFilter !== "all") {
        params.status = categoryFilter;
      }

      // Sorting
      switch (sortBy) {
        case "name-asc":
          params.sort = "name";
          params.order = "asc";
          break;
        case "name-desc":
          params.sort = "name";
          params.order = "desc";
          break;
        case "created-asc":
          params.sort = "createdAt";
          params.order = "asc";
          break;
        case "created-desc":
        default:
          params.sort = "createdAt";
          params.order = "desc";
          break;
      }

      const response = await API.getOrganizationsService(params);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const listAllPortalStates = createAsyncThunk(
  "portal/admin/states/fetch/all",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.listAllPortalStates();
      return data;
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);

export const updateOrganization = createAsyncThunk(
  "portal/admin/updateOrganization",
  async ({ id, ...organizationData }: { id: string } & any) => {
    try {
      const { data } = await API.updateOrganization(id, organizationData);
      return data;
    } catch (error) {
      throw error;
    }
  },
);

// ─── Sales: direct order creation ─────────────────────────────────────────────

export const getSalesCustomers = createAsyncThunk(
  "portal/admin/sales/customers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getSalesCustomers();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

/** States for the sales order State filter (same list the admin screens use) */
export const getSalesStates = createAsyncThunk(
  "portal/admin/sales/states",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.listAllPortalStates();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSalesCustomerGrowers = createAsyncThunk(
  "portal/admin/sales/customer/growers",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getSalesCustomerGrowers(organizationId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSalesCustomerProducts = createAsyncThunk(
  "portal/admin/sales/customer/products",
  async (
    {
      organizationId,
      cardCode,
    }: { organizationId: string; cardCode: string | string[] },
    { getState, rejectWithValue },
  ) => {
    try {
      const reduxState: any = getState();
      const { stateFilter, locationFilter } =
        reduxState.portal.admin.salesOrders;

      // Same params the superadmin web sends to the report API
      const params: Record<string, string> = {
        CardCode: Array.isArray(cardCode) ? cardCode.join(",") : cardCode,
      };
      // Group filter: both are sent together when set
      if (stateFilter && stateFilter !== "all") params.State = stateFilter;
      if (locationFilter && locationFilter !== "all")
        params.Location = locationFilter;
      const { data } = await API.getSalesCustomerProducts(
        organizationId,
        params,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createSalesOrder = createAsyncThunk(
  "portal/admin/sales/orders/create",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await API.createSalesOrder(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateSalesOrder = createAsyncThunk(
  "portal/admin/sales/orders/update",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue },
  ) => {
    try {
      // PUT /orders/sales/:id
      const { data } = await API.updateSalesOrder(id, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAppointmentsByDate = createAsyncThunk(
  "portal/appointments/by-date",
  async (
    params: {
      startDate: string;
      endDate: string;
      organizationId: string;
      calendarId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await API.getAppointmentsByDate(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
